// Device Effects
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpErrorResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { map, exhaustMap, catchError, switchMap } from 'rxjs/operators';
import { DeviceService, DeviceIdentityModel, DeviceTwinModel, DirectMethodResponse, C2DSendResponse, TelemetryMessage } from '../../services/device.service';
import * as DeviceActions from './device.actions';
import { TelemetryService } from '../../core/services/telemetry.service';

@Injectable()
export class DeviceEffects {
  private actions$ = inject(Actions);
  private deviceService = inject(DeviceService);
  private telemetry = inject(TelemetryService);

  loadIdentity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceActions.loadIdentity),
      switchMap(({ deviceId }) =>
        this.deviceService.getIdentity(deviceId).pipe(
          map((identity: DeviceIdentityModel) => DeviceActions.loadIdentitySuccess({ identity })),
          catchError((error) => {
            const msg = error?.error?.detail || error?.message || 'Failed to load device identity';
            this.telemetry.trackException(error, { action: 'loadIdentity', statusCode: error instanceof HttpErrorResponse ? String(error.status) : '' });
            return of(DeviceActions.loadIdentityFailure({ error: msg }));
          })
        )
      )
    )
  );

  loadTwin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceActions.loadTwin),
      switchMap(({ deviceId }) =>
        this.deviceService.getTwin(deviceId).pipe(
          map((twin: DeviceTwinModel) => DeviceActions.loadTwinSuccess({ twin })),
          catchError((error) => {
            const msg = error?.error?.detail || error?.message || 'Failed to load device twin';
            this.telemetry.trackException(error, { action: 'loadTwin', statusCode: error instanceof HttpErrorResponse ? String(error.status) : '' });
            return of(DeviceActions.loadTwinFailure({ error: msg }));
          })
        )
      )
    )
  );

  invokeDirectMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceActions.invokeDirectMethod),
      exhaustMap(({ deviceId, methodName, payload, timeout }) =>
        this.deviceService.invokeDirectMethod(deviceId, {
          method_name: methodName,
          payload,
          timeout,
        }).pipe(
          map((result: DirectMethodResponse) => DeviceActions.invokeDirectMethodSuccess({ result })),
          catchError((error) => {
            const msg = error?.error?.detail || error?.message || 'Direct method invocation failed';
            this.telemetry.trackException(error, { action: 'invokeDirectMethod', statusCode: error instanceof HttpErrorResponse ? String(error.status) : '' });
            return of(DeviceActions.invokeDirectMethodFailure({ error: msg }));
          })
        )
      )
    )
  );

  sendC2DMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceActions.sendC2DMessage),
      exhaustMap(({ deviceId, message }) =>
        this.deviceService.sendC2D(deviceId, message).pipe(
          map((result: C2DSendResponse) => DeviceActions.sendC2DMessageSuccess({ result })),
          catchError((error) => {
            const msg = error?.error?.detail || error?.message || 'Failed to send C2D message';
            this.telemetry.trackException(error, { action: 'sendC2DMessage', statusCode: error instanceof HttpErrorResponse ? String(error.status) : '' });
            return of(DeviceActions.sendC2DMessageFailure({ error: msg }));
          })
        )
      )
    )
  );

  loadTelemetry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceActions.loadTelemetry),
      switchMap(({ deviceId, limit }) =>
        this.deviceService.getTelemetry(deviceId, limit).pipe(
          map((response) => {
            const messages: TelemetryMessage[] = response?.messages ?? [];
            return DeviceActions.loadTelemetrySuccess({ messages });
          }),
          catchError((error) => {
            const msg = error?.error?.detail || error?.message || 'Failed to load telemetry';
            this.telemetry.trackException(error, { action: 'loadTelemetry', statusCode: error instanceof HttpErrorResponse ? String(error.status) : '' });
            return of(DeviceActions.loadTelemetryFailure({ error: msg }));
          })
        )
      )
    )
  );

  // Auto-load identity and twin when device is set
  setCurrentDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeviceActions.setCurrentDevice),
      switchMap(({ deviceId }) => [
        DeviceActions.loadIdentity({ deviceId }),
        DeviceActions.loadTwin({ deviceId }),
      ])
    )
  );
}
